import React from 'react';
import {
  ClockOutlined,
  CloseCircleOutlined,
  QuarterLoaderUniversal,
  SubtractUniversal,
  TickMarkOutlined,
} from '@duxton-web/icons';
import scribe from '@grab-x/scribe-web-sdk';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Divider, notification, Progress, Spin, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { uniqBy } from 'lodash';
import moment from 'moment';
import { Line } from 'react-chartjs-2';
import { Link } from 'react-router-dom';

import { scribeInstrumentationEvents } from '../../../common/Scribe/EventsList';
import {
  ABORTED,
  ADAPTIVE,
  CANCELLED,
  COMPLETED,
  METRIC_COLLECTION_IDS,
  METRIC_NAMES,
  PENDING_METRICS,
  REQUESTED,
  RUNNING,
  SCHEDULED,
  STARTED,
} from '../../../constants';
import Service, {
  Experiment,
  UpdateStateResponse,
  Variable,
} from '../../../lib/App';
import { retry } from '../../../utils/retry';
import { formatHasAccessErrorGeneric } from '../../../utils/url';
import { HealthCheckIcon } from '../common/DashboardColumn';
import { DEFAULT_TIME_FORMAT, TIMEZONE } from './consts';
import { getLoggedUserEmail } from '@/utils/user';

const { Text } = Typography;

dayjs.extend(utc);
dayjs.extend(timezone);

export const options = {
  maintainAspectRatio: false,
  legend: {
    display: false,
  },
  tooltips: {
    enabled: false,
  },
  scales: {
    xAxes: [
      {
        display: false,
      },
    ],
    yAxes: [
      {
        display: false,
        stacked: true,
      },
    ],
  },
};

export const commonChartConfig = {
  label: 'events',
  cubicInterpolationMode: 'monotone',
  spanGaps: true,
  borderWidth: 1,
  pointBorderWidth: 0,
  borderJoinStyle: 'miter',
  fill: true,
  borderColor: '#1890ff',
  backgroundColor: '#ecf4fd',
  lineTension: 0,
  pointRadius: 0,
  pointHitRadius: 5,
  data: [],
};

export const chartColors = ['#1890ff', '#20ab10', '#ec8d22'];

export function reorderByOwner(basicUsers) {
  const userEmail = getLoggedUserEmail();

  const isEmailValidAndNotUser = (user) => {
    return user?.email !== '' && user?.email !== userEmail;
  };

  const reorderedUsers = [
    { email: userEmail },
    ...basicUsers.filter(isEmailValidAndNotUser),
  ];

  return reorderedUsers;
}

export function DisplayBackendService({ record }) {
  const queries = record.variables?.map((variable, index) => {
    return {
      queryKey: [variable.name, index],
      queryFn: () =>
        Service.planner.fetchVariable(variable.name).then((res: any) => res),
    };
  });

  try {
    const results = useQueries(queries);

    const result = results.filter((result) => result.status === 'success')[0];
    if (result?.isLoading) return <Text>Loading...</Text>;
    if (result?.error) return <Text>Error fetching...</Text>;

    const d = result?.data as any;
    return d?.backend ? <Text>{d.backend}</Text> : <SubtractUniversal />;
  } catch (e) {
    return <SubtractUniversal />;
  }
}

export function DisplayExperimentLabel({
  experimentName,
}: {
  experimentName: string;
}) {
  const { isLoading, error, data } = useQuery({
    queryKey: ['tags', experimentName],
    queryFn: () => Service.planner.fetchExperimentTags(experimentName),
  });

  if (isLoading) {
    return <Spin />;
  }

  if (error) {
    return <Tag color="red">Error</Tag>;
  }

  if (data.ExperimentTags.length === 0) {
    return <SubtractUniversal />;
  }

  return data.ExperimentTags.map((tag: string) => (
    <Tag key={tag} color="default">
      {tag}
    </Tag>
  ));
}

export function EventCount({ experimentName }) {
  const { isLoading, error, data } = useQuery({
    queryKey: ['events', experimentName],
    queryFn: () => Service.planner.monitorExperiment(experimentName),
  });

  if (isLoading) return <Spin />;

  if (error) return <Tag color="red">Error</Tag>;

  const currentTS = new Date();
  const lastSevenDaysTS = currentTS.setDate(currentTS.getDate() - 7);
  const filteredData = data
    .filter((v) => v.points.length > 0)
    .map((d) => {
      const points = d.points.filter((p) => p[0] >= lastSevenDaysTS);
      return {
        ...d,
        points,
      };
    });

  if (!filteredData.length) {
    return <SubtractUniversal />;
  }

  const labels = uniqBy(
    filteredData
      .map((v) => v.points.map((v) => moment(v[0]).format('YYYY-MM-DD HH:mm')))
      .reduce((a, b) => [...a, ...b], []),
    null,
  );

  if (labels.length < 2) {
    return <SubtractUniversal />;
  }

  const datasets: any = filteredData.map((v, i) => ({
    ...commonChartConfig,
    borderColor: chartColors[i],
    data: v.points.map((p) => Math.round(p[1])),
  }));

  return (
    <div style={{ width: '100%', height: '2.5rem' }}>
      <Line data={{ labels, datasets }} options={options} />
    </div>
  );
}

export const rangeConfig = {
  rules: [
    { type: 'array' as const, required: false, message: 'Please select time!' },
  ],
};

export const handleApprove = async (
  experiment,
  onApproveCallback?: () => void,
) => {
  try {
    scribe.sendEvent(scribeInstrumentationEvents.clickApproveExperiment.name, {
      experiment: experiment?.name,
      status: experiment?.state,
      STATE_NAME: scribeInstrumentationEvents.clickApproveExperiment.state,
    });
    await Service.planner!.changeExperimentState(
      experiment?.name,
      'approve',
      '',
      experiment?.type === ADAPTIVE,
    ).then((res: UpdateStateResponse) => {
      if (res && res.scheduled) {
        notification.success({
          message: 'Approving an Experiment',
          description:
            'Approval flow is complete. The experiment is successfully scheduled.',
        });
      }
      if (res && !res.scheduled) {
        notification.success({
          message: 'Approving an Experiment',
          description: 'Experiment was successfully approved.',
        });
      }
      onApproveCallback?.();
    });
  } catch (error) {
    let description = (
      <span>
        Experiment request was not scheduled, due to: {error.toString()}
      </span>
    );
    if (error.response && error.response.status === 403) {
      description = formatHasAccessErrorGeneric(error);
    }
    notification.error({
      description,
      message: 'Approving an Experiment',
    });
  }
};

export function sortString(a: string, b: string) {
  return (function () {
    if (!a && !b) {
      return -1;
    }

    if (!a) {
      return 1;
    }
    if (!b) {
      return -1;
    }
    if (a === b) {
      return 0;
    }
    return a < b ? -1 : 1;
  })();
}

export const antIcon = <QuarterLoaderUniversal style={{ fontSize: 24 }} spin />;

export const getStatusIndex = ({ state }) => {
  const statusOrder = [REQUESTED, SCHEDULED, STARTED, COMPLETED];
  return statusOrder.indexOf(state) < 0
    ? statusOrder.length
    : statusOrder.indexOf(state);
};

export const extractCompletionProgress = (startDate, endDate) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const today = dayjs(new Date());

  if (start.diff(today, 'days') >= 0) {
    return 0;
  }
  if (today.diff(end, 'days') >= 0) {
    return end.diff(start, 'days');
  }

  return today.diff(start, 'days');
};

export const transformProgress = (status: string, startDate, endDate) => {
  switch (status) {
    case COMPLETED:
    case PENDING_METRICS:
      return (
        <Progress
          percent={100}
          status="active"
          strokeColor="#00B14F"
          trailColor="#D2EDEA"
          style={{ marginBottom: 0 }}
        />
      );
    case STARTED:
    case RUNNING: {
      const percent = calculatePercentageTime(startDate, endDate);
      return (
        <Progress
          percent={percent}
          status="active"
          strokeColor="#00B14F"
          trailColor="#D2EDEA"
          style={{ marginBottom: 0 }}
        />
      );
    }
    case SCHEDULED:
      return (
        <Progress
          percent={0}
          strokeColor="#00B14F"
          trailColor="#D2EDEA"
          style={{ marginBottom: 0 }}
        />
      );
    case ABORTED:
      return (
        <Progress
          percent={0}
          status="exception"
          strokeColor="#00B14F"
          trailColor="#D2EDEA"
          style={{ marginBottom: 0 }}
        />
      );
    default:
      return (
        <Progress
          percent={0}
          strokeColor="#00B14F"
          trailColor="#D2EDEA"
          style={{ marginBottom: 0 }}
        />
      );
  }
};

export const transformVariables = (variables) => {
  return variables.map((variable) => {
    return (
      <div key={variable.name}>
        <SubtractUniversal style={{ fontSize: '0.5rem' }} />{' '}
        <a
          href={`/t/variables/${variable.name}/edit?query=${variable.name}`}
          target="_blank"
          className="variable-name"
          rel="noreferrer"
        >
          <Text type="secondary">{variable.name}</Text>
        </a>
      </div>
    );
  });
};

export const transformMilliSecondTime = (time: number) => {
  if (!time) {
    return null;
  }
  return moment.unix(time).format(DEFAULT_TIME_FORMAT);
};

export const transformDate = (timestamp: number): string => {
  return dayjs.unix(timestamp).tz(TIMEZONE).format(DEFAULT_TIME_FORMAT);
};

export const transformDateFromISO = (
  timestamp: string,
  format?: string,
): string => {
  return dayjs(timestamp)
    .tz(TIMEZONE)
    .format(format || DEFAULT_TIME_FORMAT);
};

export const calculatePercentageTime = (startDate, endDate) => {
  const fromStartDateToNow = Math.abs(
    moment(new Date()).diff(startDate, 'hours'),
  );
  const totalDuration = Math.abs(
    moment(startDate).diff(moment(endDate), 'hours'),
  );
  return Math.floor((fromStartDateToNow / totalDuration) * 100);
};

export const calculateRemainingTime = (
  status: string,
  createdAt: string,
  startDate: string,
  endDate: string,
) => {
  const time = `${moment(createdAt).format('LT')} (SGT)`;
  switch (status) {
    case STARTED:
    case RUNNING: {
      if (startDate === endDate) {
        const hoursDifference = Math.abs(
          moment(startDate).diff(moment(endDate), 'hours'),
        );
        return `${hoursDifference} hours remaining till ${time}`;
      }
      const daysDifference = Math.abs(
        moment(startDate).diff(moment(endDate), 'days'),
      );
      return `${daysDifference} ${
        daysDifference > 1 ? 'days' : 'day'
      } remaining till ${time}`;
    }
    case REQUESTED:
      return (
        <Text type="warning">{`Please obtain approval by ${moment(startDate)
          .add(2, 'days')
          .calendar()} (SGT)`}</Text>
      );
    default: {
      const daysAgo = moment(endDate).fromNow();
      return `Ended ${daysAgo} at ${time}`;
    }
  }
};

export const calculateDaysDifference = (startDate, endDate) => {
  if (!endDate) {
    return null;
  }

  if (startDate === endDate) {
    const hoursDiff = Math.abs(
      moment(startDate).diff(moment(endDate), 'hours'),
    );
    return `${hoursDiff} ${hoursDiff > 1 ? 'hours' : 'hour'}`;
  }

  const daysDiff = Math.abs(moment(startDate).diff(moment(endDate), 'days'));
  return `${daysDiff} ${daysDiff > 1 ? 'days' : 'day'}`;
};

export const getColorCode = (isUnhealthy: boolean, errorMessage: string) => {
  if (errorMessage?.length > 0) return 'css-1dqifxv-error';

  switch (isUnhealthy) {
    case true:
      return 'css-1dqifxv-fail';
    case false:
      return 'css-1dqifxv-success';
    default:
      return 'css-1dqifxv-neutral';
  }
};

const SRM_DETECTED = 'SRM detected';
const SRM_NOT_DETECTED = 'SRM not detected';

export const DisplaySRM = ({ isUnhealthy, errorMessage }) => {
  let iconStyle = {
    color: `${isUnhealthy || errorMessage ? '#f5222d' : '#52c41a'}`,
  };
  let icon = isUnhealthy ? (
    <CloseCircleOutlined style={iconStyle} />
  ) : (
    <TickMarkOutlined style={iconStyle} />
  );
  return (
    <div style={{ display: 'flex', alignItems: 'baseline' }}>
      {icon}
      <h4 style={{ color: '#fff', padding: '0 4px' }}>
        {errorMessage
          ? errorMessage
          : isUnhealthy
            ? SRM_DETECTED
            : SRM_NOT_DETECTED}
      </h4>
    </div>
  );
};

interface DisplayExperimentHealthCheckState {
  isLoading: boolean;
  isUnhealthy: boolean;
  isHealthCheckIgnored: boolean;
  errorMessage: string;
}

interface DisplayExperimentHealthCheckProps {
  experiment?: Experiment;
  experimentName: string;
  status: string;
}

export class DisplayExperimentHealthCheck extends React.Component<
  DisplayExperimentHealthCheckProps,
  DisplayExperimentHealthCheckState
> {
  state = {
    isLoading: false,
    isUnhealthy: false,
    isHealthCheckIgnored: false,
    errorMessage: '',
  };
  wikipage = 'https://wiki.grab.com/display/EP/Experiment+Health+Check';
  async componentDidMount() {
    const { experiment, experimentName } = this.props;

    // [TODO] Remove once Ethan has completed pipeline for all strategies
    const isSingleVariableExperiment = experiment?.variables.length === 1;
    let isUniformChoiceOrWeightedChoice = true;
    experiment?.variables?.forEach((variable: Variable) => {
      if (!['uniformChoice', 'weightedChoice'].includes(variable.strategy)) {
        isUniformChoiceOrWeightedChoice = false;
      }
    });

    if (!isSingleVariableExperiment || !isUniformChoiceOrWeightedChoice) {
      return this.setState({ isHealthCheckIgnored: true });
    }

    switch (experiment?.state) {
      case RUNNING:
      case STARTED:
      case COMPLETED:
      case CANCELLED:
      case ABORTED:
        try {
          this.setState({ isLoading: true });
          const srmPVal = await Service.planner.getSingleMetricForExp(
            experimentName,
            METRIC_COLLECTION_IDS.srmExpLevelDailyStats,
            METRIC_NAMES.srmPVal,
          );
          const isUnhealthy = srmPVal < 0.005;
          return this.setState({ isLoading: false, isUnhealthy });
        } catch (e) {
          this.setState({
            isLoading: false,
            isUnhealthy: true,
            errorMessage: `SRM checks failed: ${e.message}`,
          });
        }
        return this.setState({ isLoading: false });
      default:
        return this.setState({ isHealthCheckIgnored: true });
    }
  }

  render() {
    const { isLoading, isHealthCheckIgnored, isUnhealthy, errorMessage } =
      this.state;

    if (isLoading) {
      return <div />;
    }

    if (isHealthCheckIgnored) {
      return (
        <ExperimentHealthCheckUnsupported
          message="SRM check unsupported at the moment"
          errorMessage={errorMessage}
          infoUrl={this.wikipage}
        />
      );
    }

    return (
      <ExperimentHealthCheckSupported
        isUnhealthy={isUnhealthy}
        errorMessage={errorMessage}
        infoUrl={this.wikipage}
      />
    );
  }
}
type ExperimentHealthCheckUnsupportedProps = {
  infoUrl: string;
  message: string;
  errorMessage: string;
};
export const ExperimentHealthCheckUnsupported = ({
  infoUrl,
  message,
  errorMessage,
}: ExperimentHealthCheckUnsupportedProps) => {
  return (
    <HealthCheckIcon
      placement="bottom"
      errorMessage={errorMessage}
      title={
        <>
          <h3 style={{ color: '#fff' }}>Experiment Health Check</h3>
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <ClockOutlined style={{ color: '#bfbfbf' }} />
            <h4 style={{ color: '#fff', padding: '0 4px' }}>
              {message}
              <br />
              {errorMessage ? '\n\n' + errorMessage : null}
            </h4>
          </div>
          <Divider style={{ margin: '0.6rem 0 0.4rem 0' }} />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Link style={{ color: '#4096ff' }} to={infoUrl} target="_blank">
              More Details
            </Link>
          </div>
        </>
      }
    />
  );
};

type ExperimentHealthCheckSupportedProps = {
  isUnhealthy: boolean;
  errorMessage: string;
  infoUrl: string;
};
export const ExperimentHealthCheckSupported = ({
  isUnhealthy,
  errorMessage,
  infoUrl,
}: ExperimentHealthCheckSupportedProps) => {
  return (
    <HealthCheckIcon
      placement="bottom"
      isUnhealthy={isUnhealthy}
      errorMessage={errorMessage}
      title={
        <>
          <h3 style={{ color: '#fff' }}>Experiment Health Check</h3>
          <DisplaySRM isUnhealthy={isUnhealthy} errorMessage={errorMessage} />
          <Divider style={{ margin: '0.6rem 0 0.4rem 0' }} />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Link style={{ color: '#4096ff' }} to={infoUrl} target="_blank">
              More Details
            </Link>
          </div>
        </>
      }
    />
  );
};

export const displayMonitoringGraph = async (expName: string) => {
  const monitorData = await retry(
    () => Service.planner.monitorExperiment(expName),
    3,
    800,
  );
  const filteredData = monitorData.filter((v) => v.points.length > 0);
  if (filteredData.length) {
    const labels = uniqBy(
      filteredData
        .map((v) =>
          v.points.map((v) => moment(v[0]).format('YYYY-MM-DD HH:mm')),
        )
        .reduce((a, b) => [...a, ...b], []),
      null,
    );
    if (labels.length < 2) {
      return {};
    }
    const datasets = filteredData.map((v, i) => ({
      ...commonChartConfig,
      borderColor: chartColors[i],
      data: v.points.map((p) => Math.round(p[1])),
    }));
    return { labels, datasets };
  }
  return { labels: [], datasets: [] };
};

export const truncate = (name: string) => {
  if (name?.length >= 40) {
    return name.slice(0, 37) + '...';
  }
  return name;
};

export const sortByDate = (startTime, endTime) => startTime - endTime;
